import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { isMobileBreakpoint, getIOSVersion, mediaQueryLarge } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

const QUICK_VIEW_CACHE_LIMIT = 4;
/** @type {Map<string, string>} */
const quickViewCache = new Map();
/** @type {AbortController | null} */
let activeQuickViewController = null;
let activeQuickViewRequestId = 0;

document.addEventListener(ThemeEvents.cartUpdate, () => quickViewCache.clear());

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController?.abort();
  }

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick view button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    if (!currentUrl) return;

    const modalContent = document.getElementById('quick-add-modal-content');
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!modalContent || !(dialogComponent instanceof QuickAddDialog)) return;

    modalContent.replaceChildren();
    modalContent.setAttribute('aria-busy', 'true');
    const loadingIndicator = document.createElement('div');
    loadingIndicator.className = 'quick-add-modal__loading';
    loadingIndicator.setAttribute('aria-hidden', 'true');
    modalContent.append(loadingIndicator);
    this.#openQuickAddModal();

    activeQuickViewController?.abort();
    const controller = new AbortController();
    this.#abortController = controller;
    activeQuickViewController = controller;
    const requestId = ++activeQuickViewRequestId;

    try {
      const productGrid = await this.fetchProductPage(currentUrl, controller.signal);
      if (requestId !== activeQuickViewRequestId) return;
      if (!productGrid) {
        modalContent?.removeAttribute('aria-busy');
        dialogComponent.closeDialog();
        return;
      }

      modalContent?.removeAttribute('aria-busy');
      await this.updateQuickAddModal(productGrid.cloneNode(true));
      this.#updateVariantPicker(productGrid);
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        console.error('Failed to load quick view:', error);
        if (requestId === activeQuickViewRequestId) {
          modalContent?.removeAttribute('aria-busy');
          dialogComponent.closeDialog();
        }
      }
    } finally {
      if (activeQuickViewController === controller) {
        activeQuickViewController = null;
      }
      if (this.#abortController === controller) {
        this.#abortController = null;
      }
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.dialogElement;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @param {AbortSignal} signal - The current quick-view request signal
   * @returns {Promise<Element | null>}
   */
  async fetchProductPage(productPageUrl, signal) {
    if (!productPageUrl) return null;

    const cachedMarkup = quickViewCache.get(productPageUrl);
    if (cachedMarkup) {
      quickViewCache.delete(productPageUrl);
      quickViewCache.set(productPageUrl, cachedMarkup);
      return new DOMParser()
        .parseFromString(cachedMarkup, 'text/html')
        .querySelector('[data-product-grid-content]');
    }

    try {
      const response = await fetch(productPageUrl, {
        signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const productGrid = html.querySelector('[data-product-grid-content]');

      if (!productGrid) {
        throw new Error('Product page did not contain quick-view content');
      }

      quickViewCache.set(productPageUrl, productGrid.outerHTML);
      if (quickViewCache.size > QUICK_VIEW_CACHE_LIMIT) {
        const oldestKey = quickViewCache.keys().next().value;
        if (oldestKey) quickViewCache.delete(oldestKey);
      }

      return productGrid;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  requiredRefs = ['quickAddDialog'];

  #abortController = new AbortController();

  get dialogElement() {
    return this.refs.quickAddDialog;
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener('click', this.#handleCloseButtonClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.removeEventListener('click', this.#handleCloseButtonClick, true);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };

  #closeQuickAddModal = () => {
    this.closeDialog();
  };

  #handleCloseButtonClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.quick-add-modal__close')) return;

    event.preventDefault();
    event.stopPropagation();
    this.closeDialog().catch((error) => console.error('Failed to close quick view:', error));
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
